<template>
  <v-card-text>
    <v-layout row wrap>
      <v-flex xs6 sm4 md3>
        <v-select
          :items="selectCalcs"
          v-model="calcs"
          label="Calculate"
          multiple
        />
      </v-flex>
      <v-flex xs6 sm4 md3>
        <v-select
          :items="selectMethods"
          v-model="meths"
          label="Using"
          multiple
        />
      </v-flex>
    </v-layout>
    <vue-apex-charts :type="group.type" :options="graphOpts" :series="series" />
  </v-card-text>
</template>

<script>
import { mapState } from 'vuex';
import VueApexCharts from 'vue-apexcharts';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import isEqual from 'lodash/isEqual';
import castArray from 'lodash/castArray';
import groups from '@/lib/groups';

export default {
  components: {
    VueApexCharts,
  },
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectCalcs: [
        { text: 'Weighted (Assessed)', value: 'weight' },
        { text: 'Per-Step', value: 'step' },
      ],
      calcs: castArray(this.$route.query.c || ['weight']),
      selectMethods: [
        { text: 'Mean Grade', value: 'mean', percent: true },
        { text: 'Mode Grade', value: 'mode', percent: true },
        { text: 'Median Grade', value: 'median', percent: true },
        { text: 'Maximum Grade', value: 'max', percent: true },
        { text: 'Minimum Grade', value: 'min', percent: true },
        { text: 'Count', value: 'count' },
        { text: 'Pass Rate', value: 'passRate', percent: true },
        { text: 'Count (No Duplicates)', value: 'countNoDupe' },
      ],
      meths: castArray(this.$route.query.m || ['mean']),
    };
  },
  computed: {
    ...mapState('attempts', ['slowPercent']),
    group() { return groups.find(g => g.value === (this.$route.query.g || 'unitId')); },
    methods() {
      const methods = [];
      let { meths, calcs } = this;
      if (!meths.length) meths = ['mean'];
      if (!calcs.length) calcs = ['weight'];
      calcs.forEach((calc) => {
        this.selectMethods.filter(m => meths.indexOf(m.value) !== -1).forEach((meth) => {
          methods.push({
            ...meth,
            calc,
            calcName: this.selectCalcs.find(c => c.value === calc).text.replace(' (Assessed)', ''),
          });
        });
      });
      return methods;
    },
    series() {
      return this.methods.map(meth => ({
        name: `${meth.calcName} ${meth.text}`,
        data: this.results.map((r) => {
          let x = get(r, this.group.label);
          let y = get(r, `${meth.calc}.${meth.value}`);

          if (meth.percent) y *= 100;

          if (this.group.x === 'numeric') {
            x = Number((x * (this.group.value === 'grade' ? 100 : 1)).toFixed(2));
          } else if (this.group.x === 'datetime') {
            x = (new Date(x || r.time)).getTime();
          } else x = `${x}`;

          return { x, y };
        }),
      }));
    },
    minmax() {
      const max = Math.max(...flatMap(this.series, s => flatMap(s.data, 'x'))) + 10;
      const min = Math.min(...flatMap(this.series, s => flatMap(s.data, 'x')), 0);
      return this.group.x === 'numeric' ? { min, max, tickAmount: Math.round((max - min) / 10) } : {};
    },
    title() {
      let title = `Grouped By: "${this.group.text}"`;
      if (this.filters.length) title += `, Filters: "${this.filters.map(f => f.name).join(', ')}"`;
      if (!this.results.length) title = 'No Data found.';
      if (this.slowPercent < 1) title = 'Loading...';
      return title;
    },
    graphOpts() {
      return {
        title: {
          text: this.title,
        },
        chart: {
          stacked: false,
          zoom: {
            type: this.group.type === 'scatter' ? 'xy' : 'x',
            enabled: this.group.x !== 'category',
          },
          toolbar: {
            autoSelected: 'zoom',
          },
          selection: {
            xaxis: { ...this.minmax },
          },
        },
        plotOptions: {
          line: {
            curve: 'smooth',
          },
        },
        dataLabels: {
          enabled: false,
        },
        // colors: ['#0165fc'],
        fill: this.group.type === 'area'
          ? {
            gradient: {
              enabled: true,
              shadeIntensity: 1,
              inverseColors: false,
              opacityFrom: 0.5,
              opacityTo: 0,
              stops: [0, 90, 100],
            },
          }
          : {},
        yaxis: {
          min: 0,
          labels: {
            formatter: v => v.toFixed(2),
          },
        },
        xaxis: {
          type: this.group.x,
          ...this.minmax,
        },
        tooltip: {
          shared: false,
          y: {
            formatter: v => v.toFixed(2),
          },
          x: {
            format: this.group.x === 'datetime' ? 'dd MMM HH:mm' : undefined,
          },
        },
      };
    },
  },
  watch: {
    meths(v) {
      const m = isEqual(v, ['mean']) ? undefined : v;
      this.$router.push({ query: { ...this.$route.query, m } });
    },
    calcs(v) {
      const c = isEqual(v, ['weight']) ? undefined : v;
      this.$router.push({ query: { ...this.$route.query, c } });
    },
  },
};
</script>

<style lang="stylus">
.apexcharts-toolbar
  z-index unset
</style>
